import { CurrencyDto } from '@api/WalletClient';
import { Ban, Check, Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useWrapRequest } from 'use-wrap-request';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { ConfirmationModal } from '../components/confirmation-modal';
import { userSearchDebounce } from '../components/hooks/use-search';
import { useSearchReducer } from '../components/hooks/useSearchReducer';
import { PageHeader } from '../components/page-header';
import { CellProps, SortingRule, TableComponent } from '../components/table';
import { Api } from '../core/api';
import { injectTSDI } from '../core/tsdi';
import { CurrencyModal } from './currency-modal';

type CurrencyRow = CurrencyDto & { id: number };

const DEFAULT_PAGE = {
    content: [] as CurrencyRow[],
    number: 0,
    size: 0,
    totalPages: 0,
    totalElements: 0
};

const StatusCell = ({
    enabled,
    enabledLabel,
    disabledLabel
}: {
    enabled: boolean;
    enabledLabel: string;
    disabledLabel: string;
}) => (
    <div className="flex items-center gap-2">
        {enabled ? (
            <Check className="size-4 text-success" />
        ) : (
            <Ban className="size-4 text-destructive" />
        )}
        <span className="text-sm">
            {enabled ? enabledLabel : disabledLabel}
        </span>
    </div>
);

const Currencies = observer(() => {
    const { walletClient } = injectTSDI(Api);
    const [createOpen, setCreateOpen] = React.useState(false);
    const [editOpen, setEditOpen] = React.useState(false);
    const [editingCurrency, setEditingCurrency] =
        React.useState<CurrencyDto | null>(null);
    const [deleteCurrency, setDeleteCurrency] =
        React.useState<CurrencyDto | null>(null);
    const [sorting, setSorting] = React.useState<SortingRule>({
        id: 'code',
        desc: false
    });

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: '-code'
    });

    const { setSearchTerm } = userSearchDebounce(dispatch);

    React.useEffect(() => {
        dispatch({ type: 'sort', sort: sorting });
    }, [sorting]);

    React.useEffect(() => {
        if (!editOpen) {
            setEditingCurrency(null);
        }
    }, [editOpen]);

    const currencies = useWrapRequest(
        async (searchParams) => {
            const result =
                await walletClient.supportApi.currencySupportControllerGetAllCurrencies(
                    searchParams
                );

            const page = result.data;
            const size = page.size || searchParams.size || 0;
            const offset = (page.number || 0) * size;

            return {
                ...page,
                content: (page.content || []).map((currency, index) => ({
                    ...currency,
                    id: offset + index + 1
                }))
            };
        },
        {
            defaultData: DEFAULT_PAGE
        }
    );

    React.useEffect(() => {
        currencies.request([searchParams]);
    }, [searchParams]);

    const refresh = () => {
        currencies.request([searchParams]);
    };

    const handleEdit = (currency: CurrencyDto) => {
        setEditingCurrency(currency);
        setEditOpen(true);
    };

    const handleDelete = async () => {
        if (!deleteCurrency) {
            return false;
        }
        await walletClient.supportApi.currencySupportControllerDeleteCurrency(
            deleteCurrency.code
        );
        setDeleteCurrency(null);
        refresh();
        return true;
    };

    return (
        <>
            <PageHeader
                title="Currencies"
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        <Plus />
                        Create currency
                    </Button>
                }
            />

            <Alert variant="info" className="mb-4">
                <AlertDescription>
                    These settings drive mobile app visibility and navigation in
                    Wallet, Rewards Shop, and Dashboard.
                </AlertDescription>
            </Alert>

            <CurrencyModal
                mode="create"
                openState={[createOpen, setCreateOpen]}
                onSuccess={() => {
                    setCreateOpen(false);
                    refresh();
                }}
            />

            <CurrencyModal
                mode="edit"
                currency={editingCurrency}
                openState={[editOpen, setEditOpen]}
                onSuccess={() => {
                    setEditOpen(false);
                    setEditingCurrency(null);
                    refresh();
                }}
            />

            <ConfirmationModal
                title="Delete currency"
                buttonText="Delete"
                destructive
                open={Boolean(deleteCurrency)}
                onModalClose={() => void setDeleteCurrency(null)}
                submitHandler={handleDelete}
            >
                <p className="text-sm text-muted-foreground">
                    This action will soft delete the currency and remove it from
                    the list.
                </p>
            </ConfirmationModal>

            <TableComponent<CurrencyRow[]>
                emptyText="No currencies available"
                items={currencies}
                columns={[
                    {
                        Header: 'Code',
                        accessor: 'code',
                        id: 'code',
                        style: { width: 150 }
                    },
                    {
                        Header: 'Name',
                        accessor: 'name',
                        id: 'name',
                        style: { width: 200 }
                    },
                    {
                        Header: 'Symbol',
                        accessor: 'symbol',
                        id: 'symbol',
                        Cell: ({
                            row: { original }
                        }: CellProps<CurrencyRow>) =>
                            original.symbolSvg?.trim() ? (
                                <div
                                    className="flex items-center [&_svg]:size-5"
                                    dangerouslySetInnerHTML={{
                                        __html: original.symbolSvg
                                    }}
                                />
                            ) : (
                                <span className="text-sm">
                                    {original.symbol}
                                </span>
                            ),
                        style: { width: 90 }
                    },
                    {
                        Header: 'Precision',
                        accessor: 'precision',
                        id: 'precision',
                        style: { width: 90 }
                    },
                    {
                        Header: 'Wallet Exchange Rate',
                        accessor: (row: CurrencyRow) => row.exchangeRateEnabled,
                        id: 'exchangeRateEnabled',
                        Cell: ({
                            row: { original }
                        }: CellProps<CurrencyRow>) => (
                            <StatusCell
                                enabled={original.exchangeRateEnabled}
                                enabledLabel="Enabled"
                                disabledLabel="Disabled"
                            />
                        ),
                        style: { width: 180 }
                    },
                    {
                        Header: 'Rewards Shop',
                        accessor: (row: CurrencyRow) => row.shopEnabled,
                        id: 'shopEnabled',
                        Cell: ({
                            row: { original }
                        }: CellProps<CurrencyRow>) => (
                            <StatusCell
                                enabled={original.shopEnabled}
                                enabledLabel="Enabled"
                                disabledLabel="Disabled"
                            />
                        ),
                        style: { width: 150 }
                    },
                    {
                        Header: 'Wallet Page',
                        accessor: (row: CurrencyRow) => row.walletEnabled,
                        id: 'walletEnabled',
                        Cell: ({
                            row: { original }
                        }: CellProps<CurrencyRow>) => (
                            <StatusCell
                                enabled={original.walletEnabled}
                                enabledLabel="Enabled"
                                disabledLabel="Disabled"
                            />
                        ),
                        style: { width: 140 }
                    },
                    {
                        Header: 'Dashboard Visibility',
                        accessor: (row: CurrencyRow) => row.hideOnDashboard,
                        id: 'hideOnDashboard',
                        Cell: ({
                            row: { original }
                        }: CellProps<CurrencyRow>) => (
                            <StatusCell
                                enabled={!original.hideOnDashboard}
                                enabledLabel="Visible"
                                disabledLabel="Hidden"
                            />
                        ),
                        style: { width: 190 },
                        disableSortBy: true
                    },
                    {
                        Header: 'Actions',
                        id: 'actions',
                        style: { width: 150 },
                        Cell: ({
                            row: { original }
                        }: CellProps<CurrencyRow>) => (
                            <div className="flex gap-1">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEdit(original)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteCurrency(original)}
                                >
                                    Delete
                                </Button>
                            </div>
                        ),
                        disableSortBy: true
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onSearchChange={(val) => void setSearchTerm(val)}
                onSortingChange={(sort) => {
                    if (sort) {
                        setSorting(sort);
                    }
                }}
                sorting={sorting}
            />
        </>
    );
});

export default Currencies;
